import { useMemo, useState } from 'react'
import type { GestureResponderEvent } from 'react-native'
import { Icon, type TIconProps } from '~/components/icon'
import type { IBottomNavigationItemProps } from '../../types/index'
import * as Comp from './bottom-item-styles'

export function BottomNavigationItem({
  key,
  disabled,
  isActive,
  icon = 'rdicon-default',
  iconActive = 'rdicon-default',
  label,
  testID,
  isFocused = false,
  accessibilityLabel,
  onPress,
  onLongPress,
  ...props
}: IBottomNavigationItemProps) {
  const [isPressed, setIsPressed] = useState(false)

  function handlePress(event: GestureResponderEvent) {
    setIsPressed(!isPressed)
  }

  const symbol = isActive ? iconActive : icon

  const iconColor = useMemo((): TIconProps['color'] => {
    if (disabled) return 'colorBrowsingTextDisabled'
    if (isActive) return 'colorBrowsingTextActivated'
    if (isPressed) return 'colorBrowsingTextPressed'
    return 'colorBrowsingTextDefault'
  }, [disabled, isActive, isPressed])

  const accessibilityProperties = {
    role: 'button',
    'aria-disabled': disabled,
    accessible: true,
    'aria-label': 'accessibilityLabel',
  } as const

  return (
    <Comp.MenuItem
      {...props}
      {...accessibilityProperties}
      key={key}
      active={isActive}
      disabled={disabled}
      onPressIn={handlePress}
      onPressOut={handlePress}
      hasPressed={isPressed}
      hasFocused={isFocused}
      testID={testID}
      onPress={onPress}
      onLongPress={onLongPress}
    >
      <Icon symbol={symbol} color={iconColor} size="small" />
      <Comp.Label
        active={isActive}
        disabled={disabled}
        hasPressed={isPressed}
        hasFocused={isFocused}
      >
        {label}
      </Comp.Label>
    </Comp.MenuItem>
  )
}
